import { IRemitsPageProps, RemitsPage } from '@enter/core';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { DataTableCard, PageLayout, PageLayoutRow } from 'components';
import { DialogContext } from 'services';
import { IObservedRootStore } from 'stores';

const LAYOUT: IRemitsPageProps['layout'] = { Page: PageLayout, Row: PageLayoutRow, Card: DataTableCard };

/**
 * Remits & Reconcile on the provider console (FE-163): core's `RemitsPage`, which Support mounts too, with this
 * console's dialogs and claim routes. It replaces the Clearinghouses page, whose ERA list and ERA items it covers.
 */
export const Remits: React.FC<IObservedRootStore> = inject('rootStore')(
    observer(({ rootStore }: IObservedRootStore) => {
        const { accountId } = useParams();
        const dialogContext = React.useContext(DialogContext);

        // Stable, so the page re-renders on its own data rather than on this wrapper's.
        const consolePieces = React.useMemo(
            (): Pick<IRemitsPageProps, 'dialogs' | 'claimHref'> => ({
                dialogs: {
                    showDialog: (Component, props): void => dialogContext.showDialog({ props, Component }),
                    hideDialog: dialogContext.hideDialog,
                    showError: dialogContext.showError,
                },
                claimHref: claimId => (accountId ? `/${accountId}/claims/${claimId}` : `/claims/${claimId}`),
            }),
            [accountId, dialogContext],
        );

        if (!rootStore) {
            return <></>;
        }

        return (
            <RemitsPage
                store={rootStore.remitsStore}
                reconcile={rootStore.reconcileStore}
                layout={LAYOUT}
                isDemo={rootStore.isDemo()}
                {...consolePieces}
            />
        );
    }),
);

/** The Clearinghouses page's old links — bookmarks, a pasted ERA link — land on Remits instead of a 404. */
export const ClearinghousesRedirect: React.FC = () => {
    const { accountId } = useParams();
    return <Navigate to={accountId ? `/${accountId}/remits` : '/remits'} replace />;
};
